import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardCheck, Printer, ChevronRight, ArrowLeft, Receipt,
  Split, Pencil, Undo2, CheckCircle2, Download, Upload, FileSpreadsheet
} from "lucide-react";
import { SkuImage } from "@/components/SkuImage";
import { SkuLabelPrintDialog } from "@/components/SkuLabelPrint";
import { CollapsibleItemRows } from "@/components/CollapsibleItems";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, tableDensity } from "@/components/ops-ui";

const FREIGHT_CODES = ["ENT-GZ", "ENT-YW", "SK-GZ", "SK-YW"];

// Round to 2 decimals but keep null/blank displayed as blank; storage stays untouched.
const r2 = (v) => (v == null || v === "" || Number.isNaN(Number(v)))
  ? ""
  : (Math.round(Number(v) * 100) / 100).toFixed(2);
const r3 = (v) => (v == null || v === "" || Number.isNaN(Number(v)))
  ? ""
  : (Math.round(Number(v) * 1000) / 1000).toFixed(3);

const downloadFile = async (url, filename) => {
  const res = await api.get(url, { responseType: "blob" });
  const blob = new Blob([res.data]);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

// ------------------------------- Root page (3 tabs) -------------------------------
export default function WarehouseArrivals() {
  const { can } = useAuth();
  const TABS = [
    { k: "pending", label: "Pending Arrival", test: "arr-tab-pending", perm: "arrival:pending_view" },
    { k: "checking", label: "Arrival (Pending Check)", test: "arr-tab-checking", perm: "arrival:check_view" },
    { k: "checked", label: "Arrived and Checked", test: "arr-tab-checked", perm: "arrival:checked_view" },
  ].filter(t => can(t.perm));
  const [tab, setTab] = useState(() => localStorage.getItem("arrivals-tab") || "pending");
  const switchTab = (v) => { setTab(v); localStorage.setItem("arrivals-tab", v); };
  useEffect(() => {
    if (TABS.length && !TABS.find(t => t.k === tab)) switchTab(TABS[0].k);
    // eslint-disable-next-line
  }, []);
  const activeTab = TABS.find(t => t.k === tab) ? tab : (TABS[0]?.k);

  return (
    <div data-testid="warehouse-arrivals-page">
      <header className="mb-4">
        <div className="caption">Warehouse operations</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Arrivals</h1>
      </header>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {TABS.map(t => (
          <button key={t.k} data-testid={t.test} onClick={() => switchTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              activeTab === t.k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>{t.label}</button>
        ))}
      </div>

      {activeTab === "pending" && <PendingArrivalSection />}
      {activeTab === "checking" && <PendingCheckSection />}
      {activeTab === "checked" && <CheckedSection />}
    </div>
  );
}

// ------------------------------- Section 1: Pending Arrival -------------------------------
function CbmBalance({ code, allocated }) {
  const [bal, setBal] = useState(null);
  const [val, setVal] = useState("");
  useEffect(() => {
    let on = true;
    api.get(`/inbound/warehouse-codes/${encodeURIComponent(code)}/cbm-balance`)
      .then(r => { if (on) { setBal(r.data); setVal(r.data.total_cbm ?? ""); } })
      .catch(() => {});
    return () => { on = false; };
  }, [code]);
  const save = async () => {
    try {
      const r = await api.put(`/inbound/warehouse-codes/${encodeURIComponent(code)}/total-cbm`,
        { total_cbm: val === "" ? null : parseFloat(val) });
      setBal(r.data);
      toast.success("Total CBM saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="flex flex-col gap-0.5" data-testid={`cbm-balance-${code}`}>
      <div className="text-[10px] text-slate-500">Allocated <b className="font-mono">{Number(allocated ?? 0).toFixed(3)}</b></div>
      <div className="flex items-center gap-1">
        <Input type="number" step="0.001" value={val} onChange={e => setVal(e.target.value)}
          onBlur={save} placeholder={Number(allocated ?? 0).toFixed(3)} className="h-7 w-20 text-xs" data-testid={`cbm-total-input-${code}`} />
        {bal && bal.total_cbm != null && (
          bal.balanced
            ? <span className="text-[10px] text-emerald-700 font-semibold" data-testid={`cbm-badge-${code}`}>✓ Bal</span>
            : <span className="text-[10px] text-amber-700 font-semibold" data-testid={`cbm-badge-${code}`}>Δ {Number(bal.balance ?? 0).toFixed(3)}</span>
        )}
      </div>
    </div>
  );
}

export function PendingArrivalSection() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [arrivalDate, setArrivalDate] = useState(() => new Date().toISOString().slice(0, 10));
  const navigate = useNavigate();
  const { density, setDensity } = useUiPrefs("arr-pending");

  const load = () => api.get("/arrivals/pending").then(r => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const toggle = (code) => {
    setSelected(prev => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === groups.length ? new Set() : new Set(groups.map(g => g.warehouse_code)));
  };

  const patchGroup = async (code, patch) => {
    try {
      await api.put(`/arrivals/warehouse-groups/${encodeURIComponent(code)}`, patch);
      toast.success(`Saved · ${code}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const checkIn = async ({ combine }) => {
    if (!selected.size) return toast.error("Select at least one warehouse code");
    if (!arrivalDate) return toast.error("Fill in the arrival date");
    try {
      const { data } = await api.post("/arrivals/check-in", {
        warehouse_codes: Array.from(selected),
        actual_arrival_date: arrivalDate,
        combine: combine && selected.size > 1,
      });
      toast.success(`Checked in ${data.count} warehouse code${data.count === 1 ? "" : "s"}${combine && selected.size > 1 ? " (combined)" : ""}`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Check-in failed"); }
  };

  const printSelected = ({ combine }) => {
    if (!selected.size) return toast.error("Select at least one warehouse code to print");
    const codes = Array.from(selected).join(",");
    navigate(`/arrivals/print?codes=${encodeURIComponent(codes)}${combine ? "&combined=1" : ""}`);
  };

  const revertToShipped = async (codes) => {
    const list = Array.isArray(codes) ? codes : [codes];
    if (!list.length) return toast.error("Select at least one warehouse code");
    if (!window.confirm(`Move ${list.length} warehouse code(s) back to Shipped? Warehouse Code + CBM will be cleared (China tracking is kept).`)) return;
    let ok = 0; const failed = [];
    for (const c of list) {
      try { await api.post(`/arrivals/warehouse-groups/${encodeURIComponent(c)}/revert-to-shipped`); ok += 1; }
      catch (e) { failed.push(c); }
    }
    if (ok) toast.success(`${ok} warehouse code(s) moved back to Shipped`);
    if (failed.length) toast.error(`Failed: ${failed.join(", ")}`);
    setSelected(new Set()); load();
  };

  const doUploadDates = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/arrivals/pending/dates-upload", fd);
      toast.success(`Updated ${data.updated} code(s)${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      if (data.errors?.length) data.errors.slice(0, 3).forEach(e => toast.warning(`${e.code}: ${e.error}`));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div data-testid="section-pending-arrival">
      <div className="flex items-center flex-wrap gap-3 mb-3 p-3 bg-slate-50 rounded border border-slate-200">
        <div className="text-xs">
          <span className="text-slate-500">Selected:</span>{" "}
          <span className="font-mono font-semibold" data-testid="pending-selected-count">{selected.size}</span>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Actual arrival date</Label>
          <Input type="date" value={arrivalDate} onChange={e => setArrivalDate(e.target.value)}
            data-testid="pending-arrival-date" className="h-8 w-36" />
        </div>
        <Button size="sm" onClick={() => checkIn({ combine: false })} data-testid="pending-check-in-standalone"
          className="bg-emerald-600 hover:bg-emerald-700" disabled={!selected.size}>
          <CheckCircle2 className="w-4 h-4 mr-1.5" />Check in ({selected.size})
        </Button>
        <Button size="sm" variant="outline" onClick={() => checkIn({ combine: true })}
          data-testid="pending-check-in-combined" disabled={selected.size < 2}>
          Combine &amp; Check in ({selected.size})
        </Button>
        <Button size="sm" variant="outline" onClick={() => revertToShipped(Array.from(selected))}
          data-testid="pending-revert-selected" disabled={!selected.size}
          className="text-amber-700 border-amber-300 hover:bg-amber-50">
          <Undo2 className="w-4 h-4 mr-1.5" />Back to Shipped ({selected.size})
        </Button>
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <Button size="sm" variant="outline" onClick={() => downloadFile("/arrivals/pending/dates-template.xlsx", "pending-arrival-dates.xlsx")}
          data-testid="pending-dates-download">
          <Download className="w-4 h-4 mr-1.5" />Download Excel
        </Button>
        <label className="inline-flex">
          <input type="file" accept=".xlsx" className="hidden" data-testid="pending-dates-upload-file"
            onChange={e => { doUploadDates(e.target.files?.[0]); e.target.value = ""; }} />
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-emerald-300 text-emerald-700 hover:bg-emerald-50 cursor-pointer">
            <Upload className="w-4 h-4" />Upload Excel
          </span>
        </label>
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <DensityToggle density={density} setDensity={setDensity} />
        <div className="text-[11px] text-slate-500 ml-2">Transfer to <b>Arrival (Pending Check)</b> only happens after you check the box + click Check in.</div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`excel-table w-full text-xs ${tableDensity(density)}`} data-testid="pending-arrival-table">
          <thead><tr>
            <th className="w-8"><Checkbox checked={selected.size > 0 && selected.size === groups.length}
              onCheckedChange={toggleAll} data-testid="pending-toggle-all" /></th>
            <th>Warehouse Code</th>
            <th className="text-right">Items</th>
            <th className="text-right">Qty</th>
            <th className="text-right">CBM</th>
            <th>Freight Co.</th>
            <th>Est. Depart</th>
            <th>Est. Arrival</th>
            <th className="text-right"></th>
          </tr></thead>
          <tbody>
            {groups.map(g => (
              <tr key={g.warehouse_code} data-testid={`pending-row-${g.warehouse_code}`}>
                <td><Checkbox checked={selected.has(g.warehouse_code)}
                  onCheckedChange={() => toggle(g.warehouse_code)}
                  data-testid={`pending-select-${g.warehouse_code}`} /></td>
                <td className="font-mono font-bold">{g.warehouse_code}</td>
                <td className="text-right font-mono">{g.items.length}</td>
                <td className="text-right font-mono">{g.total_qty}</td>
                <td><CbmBalance code={g.warehouse_code} allocated={g.total_cbm} /></td>
                <td>
                  <select defaultValue={g.freight_company_code || "ENT-GZ"}
                    onChange={e => patchGroup(g.warehouse_code, { freight_company_code: e.target.value })}
                    data-testid={`pending-freight-${g.warehouse_code}`}
                    className="h-8 border border-slate-200 rounded px-2 font-mono text-xs">
                    {FREIGHT_CODES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {g.freight_invoice_id && (
                    <Link to="/finance?tab=freight" className="block text-[11px] text-sky-600 hover:underline mt-1"
                      data-testid={`pending-freight-inv-${g.warehouse_code}`}>
                      Freight inv {g.freight_invoice_no || "→"}
                    </Link>
                  )}
                </td>
                <td><Input type="date" defaultValue={g.estimated_depart || ""} className="h-8 w-36"
                  data-testid={`pending-depart-${g.warehouse_code}`}
                  onBlur={e => { if (e.target.value !== g.estimated_depart) patchGroup(g.warehouse_code, { estimated_depart: e.target.value }); }} /></td>
                <td><Input type="date" defaultValue={g.estimated_arrival || ""} className="h-8 w-36"
                  data-testid={`pending-arrival-${g.warehouse_code}`}
                  onBlur={e => { if (e.target.value !== g.estimated_arrival) patchGroup(g.warehouse_code, { estimated_arrival: e.target.value }); }} /></td>
                <td className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" data-testid={`pending-revert-${g.warehouse_code}`}
                    onClick={() => revertToShipped(g.warehouse_code)}
                    className="text-amber-700 hover:text-amber-800 hover:bg-amber-50">
                    <Undo2 className="w-3.5 h-3.5 mr-1" />Back
                  </Button>
                  <Button variant="ghost" size="sm" data-testid={`pending-view-${g.warehouse_code}`}
                    onClick={() => navigate(`/arrivals/print?codes=${encodeURIComponent(g.warehouse_code)}`)}>
                    View <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </td>
              </tr>
            ))}
            {groups.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-slate-400">
                <ClipboardCheck className="w-6 h-6 inline-block mb-1 text-slate-300" />
                <div>Nothing pending arrival. Fill CBM + Warehouse Code on Warehouse &amp; CBM first.</div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------- Section 2: Arrival (Pending Check) -------------------------------
export function PendingCheckSection() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const navigate = useNavigate();
  const { density, setDensity } = useUiPrefs("arr-check");
  const load = () => api.get("/arrivals/pending-check").then(r => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const toggleGroup = (code) => {
    setSelected(prev => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
  };
  const printSelected = ({ combine }) => {
    if (!selected.size) return toast.error("Select at least one warehouse code to print");
    const codes = Array.from(selected).join(",");
    navigate(`/arrivals/print?codes=${encodeURIComponent(codes)}${combine ? "&combined=1" : ""}`);
  };

  const [editItem, setEditItem] = useState(null);   // {item, group}
  const [splitItem, setSplitItem] = useState(null); // {item, group}
  const [pw, setPw] = useState("");
  const [editCbm, setEditCbm] = useState("");
  const [editQty, setEditQty] = useState("");
  const [splitQty, setSplitQty] = useState("");
  const [uploadFor, setUploadFor] = useState(null); // group code
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPw, setUploadPw] = useState("");
  const [massEditFor, setMassEditFor] = useState(null);
  const [massEditFile, setMassEditFile] = useState(null);
  const [massEditPw, setMassEditPw] = useState("");
  const [massEditNeedSku, setMassEditNeedSku] = useState(null); // array of codes | null
  const [massEditSkuDrafts, setMassEditSkuDrafts] = useState({});
  const [labelSku, setLabelSku] = useState(null);

  const openEdit = (item, group) => {
    setEditItem({ item, group });
    setEditCbm(item.cbm ?? "");
    setEditQty(item.quantity ?? "");
    setPw("");
  };
  const openSplit = (item, group) => {
    setSplitItem({ item, group }); setSplitQty(""); setPw("");
  };

  const saveEdit = async () => {
    if (!editItem || !pw) return toast.error("Password required");
    try {
      await api.put(`/arrivals/items/${editItem.item.id}/edit`, {
        cbm: editCbm === "" ? null : Number(editCbm),
        quantity: editQty === "" ? null : parseInt(editQty, 10),
        password: pw,
      });
      toast.success("Item updated"); setEditItem(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const saveSplit = async () => {
    if (!splitItem || !pw) return toast.error("Password required");
    const q = parseInt(splitQty, 10);
    if (!q || q <= 0) return toast.error("Enter split quantity");
    try {
      const { data } = await api.post(`/arrivals/items/${splitItem.item.id}/split-back`, {
        split_quantity: q, password: pw,
      });
      toast.success(`Kicked ${data.kicked_back} back to Stocks in Transit`);
      setSplitItem(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Split failed"); }
  };

  const confirmCheck = async (code) => {
    if (!window.confirm(`Mark ${code} as fully checked and move to "Arrived and Checked"?`)) return;
    try {
      await api.post(`/arrivals/warehouse-groups/${encodeURIComponent(code)}/confirm-check`);
      toast.success(`${code} moved to Arrived and Checked`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Confirm failed"); }
  };

  const confirmCheckSelected = async () => {
    if (!selected.size) return toast.error("Select at least one warehouse code");
    const codes = Array.from(selected);
    if (!window.confirm(`Mark ${codes.length} warehouse code(s) as fully checked and move to "Arrived and Checked"?`)) return;
    let ok = 0; const failed = [];
    for (const code of codes) {
      try {
        await api.post(`/arrivals/warehouse-groups/${encodeURIComponent(code)}/confirm-check`);
        ok += 1;
      } catch (e) { failed.push(code); }
    }
    if (ok) toast.success(`${ok} warehouse code(s) moved to Arrived and Checked`);
    if (failed.length) toast.error(`Failed: ${failed.join(", ")}`);
    setSelected(new Set()); load();
  };

  const doUpload = async () => {
    if (!uploadFor || !uploadFile || !uploadPw) return toast.error("File and password required");
    try {
      const fd = new FormData();
      fd.append("file", uploadFile); fd.append("password", uploadPw);
      const { data } = await api.post(`/arrivals/warehouse-groups/${encodeURIComponent(uploadFor)}/edit-excel`, fd);
      toast.success(`Updated ${data.updated} row(s)`);
      setUploadFor(null); setUploadFile(null); setUploadPw(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const downloadMassEditTemplate = async (code) => {
    try {
      await downloadFile(
        `/arrivals/warehouse-groups/${encodeURIComponent(code)}/mass-edit-template.xlsx?_ts=${Date.now()}`,
        `arrival-mass-edit-${code}.xlsx`
      );
      toast.success("Mass-edit template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Template download failed");
    }
  };

  const resetMassEdit = () => {
    setMassEditFor(null); setMassEditFile(null); setMassEditPw("");
    setMassEditNeedSku(null); setMassEditSkuDrafts({});
  };

  const submitMassEdit = async (newSkusPayload) => {
    const fd = new FormData();
    fd.append("file", massEditFile);
    fd.append("password", massEditPw);
    if (newSkusPayload) fd.append("new_skus", JSON.stringify(newSkusPayload));
    try {
      const { data } = await api.post(
        `/arrivals/warehouse-groups/${encodeURIComponent(massEditFor)}/mass-edit-excel`, fd);
      toast.success(`Updated ${data.updated} item(s)${data.inserted ? ` · added ${data.inserted} new` : ""} across ${data.invoices_checked} order(s)`);
      resetMassEdit(); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.needs_sku)) {
        const drafts = {};
        detail.needs_sku.forEach(c => { drafts[c] = { name: "", item_name_2: "", variant: "", sticker_top: "", sticker_bottom: "", sticker_size: "40mm*20mm" }; });
        setMassEditSkuDrafts(drafts); setMassEditNeedSku(detail.needs_sku);
        toast.warning(`${detail.needs_sku.length} new SKU(s) need details before importing`);
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Mass update failed");
    }
  };

  const doMassEditUpload = () => {
    if (!massEditFor || !massEditFile || !massEditPw) return toast.error("File and password required");
    submitMassEdit(null);
  };

  const confirmNewSkus = () => {
    const payload = massEditNeedSku.map(c => ({ sku: c, ...massEditSkuDrafts[c] }));
    if (payload.some(p => !(p.name || "").trim())) return toast.error("Item Name is required for every new SKU");
    submitMassEdit(payload);
  };

  const printLabel = (it) => setLabelSku(it);

  return (
    <div data-testid="section-pending-check" className="space-y-4">
      <div className="flex items-center flex-wrap gap-3 p-3 bg-slate-50 rounded border border-slate-200">
        <div className="text-xs">
          <span className="text-slate-500">Selected:</span>{" "}
          <span className="font-mono font-semibold" data-testid="check-selected-count">{selected.size}</span>
        </div>
        <Button size="sm" variant="outline" onClick={() => printSelected({ combine: false })}
          data-testid="check-print-standalone" disabled={!selected.size}>
          <Printer className="w-4 h-4 mr-1.5" />Print
        </Button>
        <Button size="sm" variant="outline" onClick={() => printSelected({ combine: true })}
          data-testid="check-print-combined" disabled={selected.size < 2}>
          <Printer className="w-4 h-4 mr-1.5" />Print Combined
        </Button>
        <Button size="sm" onClick={confirmCheckSelected}
          data-testid="check-confirm-selected" disabled={!selected.size}
          className="bg-emerald-600 hover:bg-emerald-700">
          <CheckCircle2 className="w-4 h-4 mr-1.5" />Confirm Check Selected
        </Button>
        <DensityToggle density={density} setDensity={setDensity} />
        <div className="text-[11px] text-slate-500 ml-2">Tick the warehouse code(s) below, then print packing list or confirm check.</div>
      </div>
      {groups.map(g => (
        <div key={g.warehouse_code} className="rounded-lg border border-slate-200 bg-white" data-testid={`check-group-${g.warehouse_code}`}>
          <div className="flex items-center flex-wrap gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200">
            <Checkbox checked={selected.has(g.warehouse_code)} onCheckedChange={() => toggleGroup(g.warehouse_code)}
              data-testid={`check-select-${g.warehouse_code}`} />
            <div className="font-mono font-bold text-slate-900">{g.warehouse_code}</div>
            <span className="text-xs text-slate-500">
              {g.items.length} items · {g.total_qty} pcs · CBM {r3(g.total_cbm)}
            </span>
            <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-mono">{g.freight_company_code}</span>
            <span className="text-xs text-slate-500">Arrived: {g.actual_arrival_date || g.cargo_inbound_date || "—"}</span>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => downloadFile(
              `/arrivals/warehouse-groups/${encodeURIComponent(g.warehouse_code)}/edit-template.xlsx?_ts=${Date.now()}`,
              `arrival-edit-${g.warehouse_code}.xlsx`)}
              data-testid={`check-download-${g.warehouse_code}`}>
              <Download className="w-3.5 h-3.5 mr-1" />CBM Template
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setUploadFor(g.warehouse_code); setUploadPw(""); setUploadFile(null); }}
              data-testid={`check-upload-btn-${g.warehouse_code}`}>
              <Upload className="w-3.5 h-3.5 mr-1" />Upload CBM
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => downloadMassEditTemplate(g.warehouse_code)}
              data-testid={`check-mass-edit-download-${g.warehouse_code}`}>
              <Download className="w-3.5 h-3.5 mr-1" />Mass Edit Excel
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => {
                setMassEditFor(g.warehouse_code);
                setMassEditPw("");
                setMassEditFile(null);
              }}
              data-testid={`check-mass-edit-upload-${g.warehouse_code}`}>
              <Upload className="w-3.5 h-3.5 mr-1" />Upload Mass Edit
            </Button>
            <Button size="sm" onClick={() => confirmCheck(g.warehouse_code)}
              data-testid={`check-confirm-${g.warehouse_code}`}
              className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Confirm Check
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className={`excel-table w-full text-xs ${tableDensity(density)}`}>
              <thead><tr>
                <th className="w-14">Photo</th>
                <th>SKU</th>
                <th>Item Name</th>
                <th className="text-right">Qty</th>
                <th className="text-right">CNY/Pc<br/><span className="text-[10px] text-slate-400 font-normal">base</span></th>
                <th className="text-right">CNY/Pc<br/><span className="text-[10px] text-slate-400 font-normal">after adj</span></th>
                <th className="text-right">RM/Pc</th>
                <th className="text-right">CBM</th>
                <th className="w-32"></th>
              </tr></thead>
              <CollapsibleItemRows
                items={g.items}
                colSpan={9}
                testid={`check-${g.warehouse_code}`}
                renderRow={it => (
                  <tr key={it.id} data-testid={`check-row-${it.id}`}>
                    <td><SkuImage url={it.image_url} alt={it.sku} size={40} /></td>
                    <td className="font-mono font-semibold">{it.sku}</td>
                    <td className="whitespace-nowrap">{it.item_name}</td>
                    <td className="text-right font-mono">{it.quantity}</td>
                    <td className="text-right font-mono cny-badge">{r2(it.cny_unit_price)}</td>
                    <td className="text-right font-mono cny-badge">{r2(it.cny_after_adj)}</td>
                    <td className="text-right font-mono rm-badge">{r2(it.rm_unit_price_display)}</td>
                    <td className="text-right font-mono">{r3(it.cbm)}</td>
                    <td className="text-right whitespace-nowrap">
                      {!it.is_packaging && (
                        <Button variant="ghost" size="sm" onClick={() => printLabel(it)}
                          data-testid={`check-print-label-${it.id}`} title="Print label sticker">
                          <Printer className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEdit(it, g)}
                        data-testid={`check-edit-${it.id}`} title="Edit CBM/Qty (admin password)">
                        <Pencil className="w-4 h-4 text-slate-700" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openSplit(it, g)}
                        data-testid={`check-split-${it.id}`} title="Kick back partial to Stocks in Transit">
                        <Split className="w-4 h-4 text-amber-600" />
                      </Button>
                    </td>
                  </tr>
                )}
              />
            </table>
          </div>
        </div>
      ))}
      {groups.length === 0 && (
        <div className="text-center py-16 text-slate-400 bg-white rounded border border-slate-200">
          Nothing to check. Check in a group from "Pending Arrival" first.
        </div>
      )}

      {/* Edit Item Dialog */}
      <Dialog open={!!editItem} onOpenChange={v => !v && setEditItem(null)}>
        <DialogContent data-testid="check-edit-dialog">
          <DialogHeader><DialogTitle>Edit CBM / Quantity</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-500">
              SKU <span className="font-mono font-semibold text-slate-800">{editItem?.item?.sku}</span> · {editItem?.item?.item_name}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>CBM</Label>
                <Input type="number" step="0.001" value={editCbm} onChange={e => setEditCbm(e.target.value)}
                  data-testid="check-edit-cbm" />
              </div>
              <div>
                <Label>Quantity <span className="text-[10px] text-amber-600">(recalcs Mo Avg RM)</span></Label>
                <Input type="number" step="1" value={editQty} onChange={e => setEditQty(e.target.value)}
                  data-testid="check-edit-qty" />
              </div>
            </div>
            <div>
              <Label>Admin password</Label>
              <Input type="password" value={pw} onChange={e => setPw(e.target.value)}
                data-testid="check-edit-password" autoFocus
                onKeyDown={e => e.key === "Enter" && saveEdit()} />
            </div>
            <p className="text-[11px] text-slate-500">ex-freight / CNY per pc base / after adj remain unchanged.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="check-edit-save" className="bg-slate-900">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split-back Dialog */}
      <Dialog open={!!splitItem} onOpenChange={v => !v && setSplitItem(null)}>
        <DialogContent data-testid="check-split-dialog">
          <DialogHeader><DialogTitle>Kick back to Stocks in Transit</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-500">
              SKU <span className="font-mono font-semibold text-slate-800">{splitItem?.item?.sku}</span> · current qty {splitItem?.item?.quantity}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Quantity to kick back</Label>
                <button type="button" className="text-[11px] text-amber-700 font-medium hover:underline"
                  data-testid="check-split-all"
                  onClick={() => setSplitQty(String(splitItem?.item?.quantity ?? ""))}>
                  Kick all ({splitItem?.item?.quantity})
                </button>
              </div>
              <Input type="number" step="1" min={1} max={splitItem?.item?.quantity || 1}
                value={splitQty} onChange={e => setSplitQty(e.target.value)}
                data-testid="check-split-qty" placeholder={`1 – ${splitItem?.item?.quantity || 1}`} />
            </div>
            <div>
              <Label>Admin password</Label>
              <Input type="password" value={pw} onChange={e => setPw(e.target.value)}
                data-testid="check-split-password"
                onKeyDown={e => e.key === "Enter" && saveSplit()} />
            </div>
            <p className="text-[11px] text-slate-500">Kicked-back rows return to "Waiting Tracking" with the same SKU/price and no China tracking / warehouse code. Kicking the full quantity moves the whole line back.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitItem(null)}>Cancel</Button>
            <Button onClick={saveSplit} data-testid="check-split-save" className="bg-amber-600 hover:bg-amber-700">Kick Back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload CBM Excel Dialog */}
      <Dialog open={!!uploadFor} onOpenChange={v => !v && setUploadFor(null)}>
        <DialogContent data-testid="check-upload-dialog">
          <DialogHeader><DialogTitle>Upload CBM (in-place update)</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500">
              Use the downloaded template for <span className="font-mono">{uploadFor}</span>. CBM (and optional Quantity) will overwrite existing values.
            </p>
            <div>
              <Label>Excel file</Label>
              <Input type="file" accept=".xlsx" onChange={e => setUploadFile(e.target.files?.[0] || null)}
                data-testid="check-upload-file" />
            </div>
            <div>
              <Label>Admin password</Label>
              <Input type="password" value={uploadPw} onChange={e => setUploadPw(e.target.value)}
                data-testid="check-upload-password"
                onKeyDown={e => e.key === "Enter" && doUpload()} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadFor(null)}>Cancel</Button>
            <Button onClick={doUpload} data-testid="check-upload-save" className="bg-slate-900" disabled={!uploadFile}>
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!massEditFor} onOpenChange={v => !v && resetMassEdit()}>
        <DialogContent data-testid="check-mass-edit-upload-dialog">
          <DialogHeader>
            <DialogTitle>Apply arrival mass edit</DialogTitle>
            <DialogDescription data-testid="check-mass-edit-dialog-description">
              Confirm the completed spreadsheet with your admin password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500">
              Upload the mass-edit template downloaded for
              <span className="font-mono"> {massEditFor}</span>. Editable per line: <b>SKU</b>, <b>Quantity</b>, <b>Total RM</b>.
              You may add new SKU rows against an existing Order ID. Each order's total RM must stay unchanged (±0.01).
              CNY/pc, China shipping &amp; adjustment are kept from before for Master-SKU reference only.
            </p>
            <div>
              <Label>Downloaded Excel file</Label>
              <Input
                type="file"
                accept=".xlsx"
                onChange={e => setMassEditFile(e.target.files?.[0] || null)}
                data-testid="check-mass-edit-file"
              />
            </div>
            <div>
              <Label>Admin password</Label>
              <Input
                type="password"
                value={massEditPw}
                onChange={e => setMassEditPw(e.target.value)}
                onKeyDown={e => e.key === "Enter" && doMassEditUpload()}
                data-testid="check-mass-edit-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => resetMassEdit()}
              data-testid="check-mass-edit-cancel">
              Cancel
            </Button>
            <Button
              onClick={doMassEditUpload}
              disabled={!massEditFile || !massEditPw}
              className="bg-slate-900"
              data-testid="check-mass-edit-apply"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />Apply mass edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!massEditNeedSku} onOpenChange={v => { if (!v) { setMassEditNeedSku(null); setMassEditSkuDrafts({}); } }}>
        <DialogContent className="max-w-3xl" data-testid="check-new-sku-dialog">
          <DialogHeader>
            <DialogTitle>New SKUs — fill in the master details</DialogTitle>
            <DialogDescription>
              These SKUs from your Excel are not in the master list. Fill them in to create them, then the import continues automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {(massEditNeedSku || []).map(code => {
              const d = massEditSkuDrafts[code] || {};
              const set = (k, v) => setMassEditSkuDrafts(prev => ({ ...prev, [code]: { ...prev[code], [k]: v } }));
              return (
                <div key={code} className="rounded border border-slate-200 p-3" data-testid={`new-sku-card-${code}`}>
                  <div className="font-mono font-bold text-sm mb-2">{code}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <Label className="text-xs">Item Name *</Label>
                      <Input value={d.name || ""} onChange={e => set("name", e.target.value)} data-testid={`new-sku-name-${code}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Item Name 2</Label>
                      <Input value={d.item_name_2 || ""} onChange={e => set("item_name_2", e.target.value)} data-testid={`new-sku-name2-${code}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Variant</Label>
                      <Input value={d.variant || ""} onChange={e => set("variant", e.target.value)} data-testid={`new-sku-variant-${code}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Label 1st line</Label>
                      <Input value={d.sticker_top || ""} onChange={e => set("sticker_top", e.target.value)} data-testid={`new-sku-top-${code}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Label 2nd line</Label>
                      <Input value={d.sticker_bottom || ""} onChange={e => set("sticker_bottom", e.target.value)} data-testid={`new-sku-bottom-${code}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Label size</Label>
                      <select value={d.sticker_size || "40mm*20mm"} onChange={e => set("sticker_size", e.target.value)}
                        data-testid={`new-sku-size-${code}`} className="h-9 w-full border border-slate-200 rounded px-2 text-sm">
                        <option value="30mm*20mm">30mm*20mm</option>
                        <option value="40mm*20mm">40mm*20mm</option>
                        <option value="70mm*30mm">70mm*30mm</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMassEditNeedSku(null); setMassEditSkuDrafts({}); }} data-testid="check-new-sku-cancel">Cancel</Button>
            <Button onClick={confirmNewSkus} className="bg-slate-900" data-testid="check-new-sku-confirm">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />Create &amp; apply import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SkuLabelPrintDialog open={!!labelSku} onOpenChange={v => !v && setLabelSku(null)} sku={labelSku} allowQuantityToggle={true} />
    </div>
  );
}

// ------------------------------- Section 3: Arrived and Checked -------------------------------
export function CheckedSection() {
  const [groups, setGroups] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [wh, setWh] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const { density, setDensity } = useUiPrefs("arr-checked");

  const load = async () => {
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (wh) p.set("wh", wh);
    const { data } = await api.get(`/arrivals/checked?${p.toString()}`);
    setGroups(data);
    setSelected(new Set());
  };
  useEffect(() => { load(); }, []);

  const toggle = (code) => setSelected(prev => {
    const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n;
  });
  const toggleAll = () => setSelected(prev =>
    prev.size === groups.length ? new Set() : new Set(groups.map(g => g.warehouse_code)));

  const doExport = () => {
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (wh) p.set("wh", wh);
    downloadFile(`/arrivals/checked/export.xlsx?${p.toString()}`, `arrivals-checked-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const doExportSelected = () => {
    if (!selected.size) return toast.error("Select at least one warehouse code to export");
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (wh) p.set("wh", wh);
    p.set("codes", Array.from(selected).join(","));
    downloadFile(`/arrivals/checked/export.xlsx?${p.toString()}`, `arrivals-checked-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const undoCheck = async (code) => {
    if (!window.confirm(`Undo check for ${code}? It will return to "Arrival (Pending Check)".`)) return;
    try {
      await api.post(`/arrivals/warehouse-groups/${encodeURIComponent(code)}/undo-check`);
      toast.success(`${code} returned to Pending Check`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
  };

  return (
    <div data-testid="section-checked">
      <div className="flex flex-wrap items-end gap-3 mb-3 p-3 bg-slate-50 rounded border border-slate-200">
        <div>
          <Label className="text-xs">Date From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            data-testid="checked-date-from" className="h-8 w-36" />
        </div>
        <div>
          <Label className="text-xs">Date To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            data-testid="checked-date-to" className="h-8 w-36" />
        </div>
        <div>
          <Label className="text-xs">Warehouse Code</Label>
          <Input value={wh} onChange={e => setWh(e.target.value)} placeholder="filter…"
            data-testid="checked-wh-filter" className="h-8 w-32" />
        </div>
        <Button size="sm" onClick={load} data-testid="checked-filter-apply">Apply</Button>
        <DensityToggle density={density} setDensity={setDensity} />
        <div className="flex-1" />
        <div className="text-xs mr-1">
          <span className="text-slate-500">Selected:</span>{" "}
          <span className="font-mono font-semibold" data-testid="checked-selected-count">{selected.size}</span>
        </div>
        <Button size="sm" onClick={doExportSelected} data-testid="checked-export-selected" disabled={!selected.size}>
          <Download className="w-3.5 h-3.5 mr-1" />Export Selected
        </Button>
        <Button size="sm" variant="outline" onClick={doExport} data-testid="checked-export">
          <Download className="w-3.5 h-3.5 mr-1" />Export All
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`excel-table w-full text-xs ${tableDensity(density)}`} data-testid="checked-table">
          <thead><tr>
            <th className="w-8"><Checkbox checked={groups.length > 0 && selected.size === groups.length}
              onCheckedChange={toggleAll} data-testid="checked-toggle-all" /></th>
            <th>Warehouse Code</th>
            <th>Checked At</th>
            <th className="text-right">Items</th>
            <th className="text-right">Qty</th>
            <th className="text-right">CBM</th>
            <th>Freight Co.</th>
            <th className="text-right"></th>
          </tr></thead>
          <tbody>
            {groups.map(g => (
              <tr key={g.warehouse_code} data-testid={`checked-row-${g.warehouse_code}`}>
                <td><Checkbox checked={selected.has(g.warehouse_code)} onCheckedChange={() => toggle(g.warehouse_code)}
                  data-testid={`checked-select-${g.warehouse_code}`} /></td>
                <td className="font-mono font-bold">{g.warehouse_code}</td>
                <td className="font-mono text-slate-600">{(g.checked_at || "").slice(0, 10)}</td>
                <td className="text-right font-mono">{g.items.length}</td>
                <td className="text-right font-mono">{g.total_qty}</td>
                <td className="text-right font-mono">{r3(g.total_cbm)}</td>
                <td><span className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100">{g.freight_company_code}</span></td>
                <td className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => undoCheck(g.warehouse_code)}
                    data-testid={`checked-undo-${g.warehouse_code}`}
                    className="text-amber-700 hover:bg-amber-50">
                    <Undo2 className="w-4 h-4 mr-1" />Undo
                  </Button>
                </td>
              </tr>
            ))}
            {groups.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">
                No checked arrivals in this filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Inline auto-shrink text for the item_name_2 header row.
// Renders at base font-size, then shrinks down (min 6.5pt) until it fits its parent's width.
// Only the target header row shrinks — other rows keep their normal size.
function AutoFitInline({ text, basePt = 9.5, minPt = 6.5 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let size = basePt;
    el.style.fontSize = `${size}pt`;
    const parent = el.parentElement;
    if (!parent) return;
    // small guard for tests / hidden containers
    if (parent.clientWidth === 0) return;
    // Try to shrink until it fits
    while (size > minPt && el.scrollWidth > parent.clientWidth - 4) {
      size -= 0.25;
      el.style.fontSize = `${size}pt`;
    }
  }, [text]);
  return <span ref={ref} className="font-semibold whitespace-nowrap"
    style={{ fontSize: `${basePt}pt` }}>{text}</span>;
}

// Auto-fit a single-line cell (SKU / qty) to its fixed-width column using actual rendered-width
// measurement. Grows the font toward ~90% of usable cell width when there's spare space, and
// shrinks it when the value is too wide — so short values look large and long values only shrink
// as much as needed. Never wraps, truncates, ellipsis or clips. Column width is untouched.
function AutoFitCell({ text, basePt = 9, minPt = 4, maxPt = 12, weight = 500, align = "left", dep }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const td = el.parentElement;
    if (!td || td.clientWidth === 0) { el.style.fontSize = `${basePt}pt`; return; }
    const usable = (td.clientWidth - 8) * 0.975;   // td padding 4px*2 + ~2.5% safety margin
    const target = usable * 0.90;                  // aim to fill ~90% of usable width
    let size = basePt;
    el.style.fontSize = `${size}pt`;
    let guard = 0;
    while (size < maxPt && el.scrollWidth < target && guard++ < 200) {
      size += 0.25; el.style.fontSize = `${size}pt`;
    }
    guard = 0;
    while (size > minPt && el.scrollWidth > usable && guard++ < 200) {
      size -= 0.25; el.style.fontSize = `${size}pt`;
    }
  }, [text, dep, basePt, minPt, maxPt]);
  return (
    <span ref={ref}
      className="inline-block max-w-full whitespace-nowrap overflow-hidden align-middle"
      style={{ fontSize: `${basePt}pt`, fontWeight: weight }}>{text}</span>
  );
}

// ------------------------------- Print Page (A5 Portrait) -------------------------------

// ITEM cell: renders "Item Name 2 - Variant" on ONE line, shrinking font per the spec:
//  1) shrink Item Name 2 only (Variant stays) until Item Name 2 is up to 4pt smaller than Variant;
//  2) if still overflowing, shrink both together keeping the 4pt gap. Never wraps/clips.
// Falls back to the original item name when Item Name 2 / Variant are unavailable.
function ItemCell({ name2, variant, fallback, dep }) {
  const wrapRef = useRef(null);
  const n2Ref = useRef(null);
  const vRef = useRef(null);
  const twoPart = !!(name2 && variant);
  const single = twoPart ? null : (name2 || fallback || "");
  const BASE = 8, FLOOR = 4;
  useEffect(() => {
    const wrap = wrapRef.current; if (!wrap) return;
    const td = wrap.parentElement; if (!td || !td.clientWidth) return;
    const avail = (td.clientWidth - 8) * 0.975;   // td padding 4px*2 + ~2.5% safety margin
    let guard = 0;
    if (twoPart) {
      let V = BASE, N = BASE;
      const apply = () => {
        if (n2Ref.current) n2Ref.current.style.fontSize = `${N}pt`;
        if (vRef.current) vRef.current.style.fontSize = `${V}pt`;
      };
      apply();
      while (wrap.scrollWidth > avail && N > V - 3 && guard++ < 60) { N -= 0.5; apply(); }
      guard = 0;
      while (wrap.scrollWidth > avail && V > FLOOR + 3 && guard++ < 80) { V -= 0.5; N = V - 3; apply(); }
    } else {
      const el = n2Ref.current; if (!el) return;
      let F = BASE; el.style.fontSize = `${F}pt`;
      while (wrap.scrollWidth > avail && F > FLOOR && guard++ < 80) { F -= 0.5; el.style.fontSize = `${F}pt`; }
    }
  }, [name2, variant, fallback, dep, twoPart]);
  return (
    <div ref={wrapRef} className="a5-item-wrap">
      {twoPart ? (
        <>
          <span ref={n2Ref} className="a5-item-n2" style={{ fontSize: `${BASE}pt`, fontWeight: 400 }}>{name2}</span>
          <span ref={vRef} className="a5-item-var" style={{ fontSize: `${BASE}pt`, fontWeight: 500 }}> - {variant}</span>
        </>
      ) : (
        <span ref={n2Ref} className="a5-item-var" style={{ fontSize: `${BASE}pt` }}>{single}</span>
      )}
    </div>
  );
}
// Title Case: capitalize the first letter of every word — used only on Arrivals print page.
const titleCase = (s) => {
  if (!s) return "";
  return String(s).toLowerCase()
    .replace(/(^|\s|-|\/|\(|\[)([a-z])/g, (m, p, c) => p + c.toUpperCase());
};

// Legacy dash-split dedup (fallback when item_name_2 / variant not populated).
const dedupWithinOrder = (firstName, currentName) => {
  if (!firstName || !currentName) return currentName;
  if (firstName === currentName) return currentName;
  const sep = " - ";
  const A = String(firstName).split(sep);
  const B = String(currentName).split(sep);
  let n = 0;
  while (n < A.length && n < B.length &&
         A[n].trim().toLowerCase() === B[n].trim().toLowerCase()) n++;
  if (n < 2) return currentName;
  return B.slice(n - 1).join(sep);
};

export function WarehouseArrivalDetail() {
  const [params] = useSearchParams();
  const codes = (params.get("codes") || "").split(",").map(s => s.trim()).filter(Boolean);
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const [headerText, setHeaderText] = useState("");
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    if (!codes.length) return;
    api.get(`/arrivals/print-view?codes=${encodeURIComponent(codes.join(","))}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Load failed"));
  }, [params.get("codes")]);

  // Warehouse-code header: always show the FULL codes on one line, shrinking the font if needed.
  useEffect(() => {
    if (!data) return;
    setHeaderText(data.header_full || data.combined_header || "");
  }, [data]);

  useEffect(() => {
    const el = headerRef.current;
    if (!el || !headerText) return;
    const parentW = el.parentElement?.clientWidth || 0;
    if (!parentW) return;
    let size = 20;                                       // always show FULL codes; shrink to fit one line
    el.style.fontSize = "";
    el.style.setProperty("--a5-header-size", `${size}pt`);
    while (el.scrollWidth > parentW && size > 6) {
      size -= 0.5;
      el.style.setProperty("--a5-header-size", `${size}pt`);
    }
  }, [headerText, data]);

  // Re-run per-item font fitting once the Roboto Condensed webfont is actually loaded.
  useEffect(() => {
    let alive = true;
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => { if (alive) setFontsReady(true); });
    } else {
      setFontsReady(true);
    }
    return () => { alive = false; };
  }, []);

  // Build print structure: each warehouse group -> a titled section with one table per order-id.
  const printGroups = useMemo(() => {
    if (!data) return [];
    return (data.groups || []).map(g => {
      const byOrder = new Map();
      g.items.forEach(it => {
        const k = it.order_id || "__no_order__";
        if (!byOrder.has(k)) byOrder.set(k, []);
        byOrder.get(k).push(it);
      });
      return {
        warehouse_code: g.warehouse_code,
        orders: Array.from(byOrder.entries()).map(([order_id, items]) => ({ order_id, items })),
      };
    });
  }, [data]);

  if (!codes.length) return <div className="text-slate-400 text-sm">Missing codes.</div>;
  if (!data) return <div className="text-slate-400 text-sm">Loading…</div>;

  return (
    <div data-testid="warehouse-arrival-print">
      <div className="print:hidden mb-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/arrivals")} data-testid="arr-print-back">
          <ArrowLeft className="w-4 h-4 mr-1" />Back to Arrivals
        </Button>
        <Button onClick={() => window.print()} data-testid="arr-print-btn" className="bg-slate-900">
          <Printer className="w-4 h-4 mr-1.5" />Print (A5)
        </Button>
      </div>

      <div className="a5-print-sheet bg-white rounded border border-slate-200 px-4 py-3 print:border-0 print:p-0">
        <header className="mb-2 text-center">
          <h1 ref={headerRef} className="a5-print-header a5-combined-title leading-tight text-slate-900 text-center mx-auto" data-testid="arr-print-header">
            {headerText}
          </h1>
        </header>
        {printGroups.some(g => g.orders.some(o => o.items.some(it => it.needs_arrival_check))) && (
          <div className="text-[10px] text-amber-800 mb-1 text-center" data-testid="arr-print-check-legend">
            <span className="inline-block px-1 font-bold" style={{ background: "#fbbf24", boxShadow: "inset 0 0 0 1.2px #b45309", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>QTY *</span> = quantity to verify on arrival
          </div>
        )}

        {printGroups.map(g => (
          <section key={g.warehouse_code} className="a5-wh-section" data-testid={`print-wh-section-${g.warehouse_code}`}>
            <div className="a5-wh-title" data-testid={`print-wh-header-${g.warehouse_code}`}>{g.warehouse_code}</div>
            {g.orders.map(o => (
              <table key={o.order_id} className="a5-print-table a5-pl-table w-full">
                <colgroup>
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "64%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "8%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="text-left">SKU</th>
                    <th className="text-center">ITEM</th>
                    <th className="text-center">QTY</th>
                    <th></th>
                    <th className="text-center">Rack</th>
                  </tr>
                </thead>
                <tbody>
                  {o.items.map(it => (
                    <tr key={it.id} data-testid={`print-row-${it.id}`}
                      style={it.needs_arrival_check ? { background: "#fff7ed" } : undefined}>
                      <td className="a5-sku"><AutoFitCell text={it.sku} basePt={9} minPt={4} maxPt={13} weight={500} dep={fontsReady} /></td>
                      <td>
                        <ItemCell name2={(it.item_name_2 || "").trim()} variant={(it.variant || "").trim()}
                          fallback={it.item_name || ""} dep={fontsReady} />
                      </td>
                      <td className={`a5-qty text-center${it.needs_arrival_check ? " a5-qty-check" : ""}`}><AutoFitCell text={String(it.quantity ?? "") + (it.needs_arrival_check ? " *" : "")} basePt={9} minPt={4} maxPt={12} weight={600} dep={fontsReady} /></td>
                      <td className="border-l border-slate-300">&nbsp;</td>
                      <td className="a5-rack text-center border-l border-slate-300">{it.rack_code || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
